import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useUserContext } from '@/auth/UserContext';
import { Button } from '@/components/Button';
import { ProfileCard } from '@/components/Cards/ProfileCard';
import { Icon } from '@/components/Icons';
import { Modal } from '@/components/Modal';
import { useProfileMenu } from '@/components/ProfileMenu/ProfileMenuProvider';
import { useCloseOnBreakpointChange } from '@/hooks/useCloseOnBreakpointChange';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useSwipeToClose } from '@/hooks/useSwipeToClose';
import { useSwitchProfile } from '@/hooks/useSwitchProfile';
import { ROUTE_PROFILE_SELECTION } from '@/router/routes';
import {
  ButtonVariant,
  IconType,
  ModalSize,
  ModalVariant,
  Size,
} from '@/types/components';
import type { ProfileIdentity } from '@/types/user';
import {
  getProfileKey,
  getRoleLabel,
  isSameProfile,
  personTypeIcons,
} from '@/utils/person';
import { joinNonEmpty } from '@/utils/text';
import { tv } from '@/utils/tv';

const trigger = tv({
  base: 'relative flex w-full cursor-pointer items-center justify-between gap-4 rounded-[10px] px-3 py-2 transition-shadow md:rounded-xl',
  variants: {
    isOpen: {
      true: 'bg-background',
      false: 'bg-popover shadow-card hover:shadow-none',
    },
  },
});

const chevron = tv({
  base: 'size-4 shrink-0 text-muted-foreground transition-transform',
  variants: {
    isOpen: { true: 'rotate-180' },
  },
});

export const ProfileDropdown: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentProfile, currentStudyYear, profiles } = useUserContext();
  const { switchProfile } = useSwitchProfile();
  const { open: openProfileMenu } = useProfileMenu();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { sheetRef, swipeProps } = useSwipeToClose(() => setIsOpen(false));

  const selectedYearProfiles = currentStudyYear?.profiles ?? [];
  const hasNotifications = (profiles?.notificationCount ?? 0) > 0;
  const studyYearLabel = currentStudyYear
    ? t('profile.studyYearName', { name: currentStudyYear.name })
    : '';

  const handleProfileClick = (identity: ProfileIdentity) => {
    setIsOpen(false);

    if (currentProfile && isSameProfile(identity, currentProfile.identity)) {
      return;
    }

    switchProfile(identity);
  };

  const handleAllProfilesClick = () => {
    setIsOpen(false);
    navigate(ROUTE_PROFILE_SELECTION);
  };

  useCloseOnBreakpointChange(isOpen, () => setIsOpen(false));

  useEffect(() => {
    if (!isOpen || isMobile) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) {
        return;
      }

      setIsOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isMobile]);

  if (!currentProfile) {
    return null;
  }

  return (
    <div className="flex w-full min-w-0 items-center justify-between gap-2 md:justify-end md:gap-7">
      <div
        ref={containerRef}
        className="relative w-fit min-w-[100px] max-w-full md:ml-auto"
      >
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className={trigger({ isOpen })}
        >
          {hasNotifications && (
            <span className="absolute -right-[3px] -top-[3px] size-[14px] rounded-full bg-accent ring-2 ring-card" />
          )}
          <div className="flex min-w-0 flex-col">
            <div className="flex min-w-0 items-baseline gap-1">
              <p className="text-subhead-14 truncate text-foreground md:text-primary">
                {currentProfile.user.firstName} {currentProfile.user.lastName}
              </p>
              <p className="text-caption-12 shrink-0 text-muted-foreground md:hidden">
                ({getRoleLabel(currentProfile.user.personType)})
              </p>
            </div>
            {(currentProfile.user.className ||
              currentProfile.user.schoolName) && (
              <p className="text-caption-12 truncate text-left text-muted-foreground">
                {joinNonEmpty([
                  currentProfile.user.className,
                  currentProfile.user.schoolName,
                ])}
              </p>
            )}
          </div>
          <Icon icon={IconType.ChevronDown} className={chevron({ isOpen })} />
        </button>

        {isOpen && !isMobile && (
          <div className="absolute right-0 top-full z-50 mt-4 max-h-[85vh] w-[359px] overflow-y-auto rounded-[14px] bg-card p-2 shadow-card [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex items-center justify-between gap-3">
              <Link
                to={ROUTE_PROFILE_SELECTION}
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-2 p-2 text-subhead-14 text-primary"
              >
                <Icon icon={IconType.Return} className="size-4 shrink-0" />
                {t('profile.dropdownAllProfilesLink')}
              </Link>
              <p className="text-caption-12 shrink-0 pr-px text-subtle-foreground">
                {studyYearLabel}
              </p>
            </div>

            <div className="flex flex-col gap-1 pt-3">
              {selectedYearProfiles.map((profile) => (
                <ProfileCard
                  key={getProfileKey(profile.identity)}
                  firstName={profile.user.firstName}
                  lastName={profile.user.lastName}
                  className={profile.user.className}
                  schoolName={profile.user.schoolName}
                  personType={profile.user.personType}
                  notificationCount={profile.notifications.count}
                  onClick={() => handleProfileClick(profile.identity)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        <p className="hidden text-callout-16 text-foreground capitalize md:block">
          {getRoleLabel(currentProfile.user.personType)}
        </p>
        <button
          type="button"
          onClick={() => openProfileMenu()}
          aria-label={t('profileMenu.title')}
          className="shrink-0 cursor-pointer rounded-full outline-none"
        >
          <Icon
            icon={personTypeIcons[currentProfile.user.personType]}
            className="size-12 shrink-0 rounded-full border border-transparent contrast:border-border"
          />
        </button>
      </div>

      {isMobile && (
        <Modal
          isOpen={isOpen}
          label={t('profile.sheetTitle')}
          variant={ModalVariant.BottomSheet}
          size={ModalSize.Small}
          contentRef={sheetRef}
          onClose={() => setIsOpen(false)}
        >
          <div
            {...swipeProps}
            className="flex shrink-0 cursor-grab flex-col touch-none active:cursor-grabbing"
          >
            <div className="flex justify-center pt-2 pb-1">
              <div className="h-1 w-10 rounded-full bg-drag-handle" />
            </div>
            <div className="flex flex-col gap-2 px-5 pt-3 pb-3">
              <h2 className="text-title-36 text-foreground">
                {t('profile.sheetTitle')}
              </h2>
              <p className="text-body-13 text-subtle-foreground">
                {studyYearLabel}
              </p>
            </div>
          </div>

          <div className="flex flex-1 flex-col gap-1 overflow-y-auto overscroll-contain px-5 pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {selectedYearProfiles.map((profile) => (
              <ProfileCard
                key={getProfileKey(profile.identity)}
                firstName={profile.user.firstName}
                lastName={profile.user.lastName}
                className={profile.user.className}
                schoolName={profile.user.schoolName}
                personType={profile.user.personType}
                notificationCount={profile.notifications.count}
                onClick={() => handleProfileClick(profile.identity)}
              />
            ))}
          </div>

          <div className="flex shrink-0 border-t border-divider bg-background px-4 pt-4 pb-[calc(24px+env(safe-area-inset-bottom))]">
            <Button
              variant={ButtonVariant.Secondary}
              size={Size.Large}
              onClick={handleAllProfilesClick}
              className="w-full"
            >
              {t('profile.dropdownAllProfilesLink')}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};
